/**
 * CommissionAuditDetailPage — iter-264e (Feb 2026)
 * ================================================
 * Route: /manage/cloudgm-agent/commission-audit/:subject_type/:subject_id
 *
 * Dedicated auditor workspace per user's directive (task B). Replaces the
 * inline row-level upload/note/mark buttons with a proper detail page:
 *
 *   1. ارفع سند الإيداع (AI verifies amount matches company_commission).
 *   2. "هل يوجد تجديد للرخصة؟" — Yes/No.
 *        · If Yes → pick renewal level (1..5).
 *   3. "هل يوجد عمولة للوكيل؟" — Yes/No.
 *   4. Save the metadata + click "تم التدقيق".
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, ShieldCheck, Upload, Loader2, CheckCircle2, XCircle,
  RotateCw, User, Building2, RefreshCw, Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { AgentPageShell } from './agent/AgentPageShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CommissionAuditDetailPage() {
  const { subject_type: subjectType = 'company_registration', subject_id: subjectId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState(null);

  // ── Local state ──────────────────────────────────────────────────
  const [file, setFile] = useState(null);           // File object (before upload)
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);   // {file_id, filename}
  const [aiScanning, setAiScanning] = useState(false);
  const [aiResult, setAiResult] = useState(null);   // {amount, verified, info}
  const [note, setNote] = useState('');

  // Q1: Is this a renewal? · Q2: per-renewal state map
  // iter-279c (Feb 2026) — Multi-select renewals with per-renewal agent
  // commission Y/N. Structure: { level: 'yes'|'no'|null }
  //   { 1: 'yes', 2: 'yes', 3: 'no' }   ← levels 1,2 have agent commission,
  //                                       level 3 has renewal (company) commission
  //                                       but NO agent commission.
  // A missing key = renewal level NOT selected (i.e. no commission at all).
  const [isRenewal, setIsRenewal] = useState(null);           // 'yes'|'no'|null
  const [renewalStates, setRenewalStates] = useState({});     // {level: 'yes'|'no'|null}

  const [saving, setSaving] = useState(false);

  // ── Load ────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/commission-audit/${subjectId}`,
        { headers: getAuthHeaders(), params: { subject_type: subjectType } },
      );
      setSummary(r.data?.summary || null);
      const a = r.data?.audit || null;
      setAudit(a);
      if (a) {
        setIsRenewal(a.is_renewal ? 'yes' : (a.audited ? 'no' : null));
        // iter-279c — Rebuild the per-renewal state map from either the
        // new `renewal_states` field or the legacy renewal_level/has_agent_commission.
        if (a.renewal_states && typeof a.renewal_states === 'object') {
          setRenewalStates(a.renewal_states);
        } else if (a.renewal_level) {
          setRenewalStates({ [a.renewal_level]: a.has_agent_commission ? 'yes' : 'no' });
        } else {
          setRenewalStates({});
        }
        setNote(a.note || '');
        if (a.receipt?.file_id) {
          setUploadedFile({ file_id: a.receipt.file_id, filename: a.receipt.filename || '' });
          setAiResult({ amount: a.receipt.amount || 0, verified: true, info: '' });
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [subjectId, subjectType]);

  useEffect(() => { load(); }, [load]);

  // ── Upload + AI verify ──────────────────────────────────────────
  const doUploadAndVerify = async (f) => {
    if (!f) return;
    setFile(f);
    setUploading(true);
    setAiResult(null);
    try {
      // 1. Upload the file
      const fd = new FormData();
      fd.append('file', f);
      const up = await axios.post(`${API}/upload/file`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const info = { file_id: up.data.id || up.data.file_id, filename: f.name };
      setUploadedFile(info);
      toast.success(t('تم رفع الإيصال', 'Receipt uploaded'));

      // 2. Run AI verification against company_commission
      setUploading(false);
      setAiScanning(true);
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      const expected = Number(summary?.company_commission || 0);
      const ai = await axios.post(
        `${API}/admin/company-registrations/extract-receipt`,
        { file_base64: dataUrl, mime: f.type, expected_amount: expected, receipt_type: null },
        { headers: getAuthHeaders() },
      );
      const data = ai.data?.data || {};
      const readAmt = data.amount ? Number(data.amount) : 0;
      const matches = readAmt > 0 && Math.abs(readAmt - expected) < 0.01;
      setAiResult({
        amount:   readAmt,
        verified: matches,
        info: matches
          ? t(`✓ AI: ${fmt(readAmt)} = المطلوب ${fmt(expected)}`, `✓ AI: ${fmt(readAmt)} matches expected ${fmt(expected)}`)
          : t(
              `❌ AI قرأ ${fmt(readAmt)} — لا يطابق عمولة الشركة ${fmt(expected)}`,
              `❌ AI read ${fmt(readAmt)} — does not match company commission ${fmt(expected)}`,
            ),
      });
      if (matches) toast.success(t('✓ الإيصال مطابق لعمولة الشركة', '✓ Receipt matches company commission'));
      else toast.error(t('مبلغ الإيصال لا يطابق عمولة الشركة', 'Receipt amount does not match company commission'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      setAiScanning(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setUploadedFile(null);
    setAiResult(null);
  };

  // ── Save + mark audited ─────────────────────────────────────────
  // iter-279c — Multi-select renewals with per-renewal agent commission.
  //   isRenewal === 'no'  → renewalStates is empty; skip nested check.
  //   isRenewal === 'yes' → ≥1 renewal selected AND every selected must answer agent Y/N.
  const selectedLevels = useMemo(
    () => Object.keys(renewalStates).map(Number).filter(n => Number.isFinite(n)).sort((a, b) => a - b),
    [renewalStates],
  );
  const canSubmit = useMemo(() => (
    !!uploadedFile
    && !!aiResult?.verified
    && isRenewal !== null
    && (
      isRenewal !== 'yes'
      || (selectedLevels.length > 0 && selectedLevels.every(l => renewalStates[l] === 'yes' || renewalStates[l] === 'no'))
    )
  ), [uploadedFile, aiResult, isRenewal, selectedLevels, renewalStates]);

  const submit = async () => {
    if (!canSubmit) {
      toast.error(t('أكمل جميع الحقول قبل التدقيق', 'Complete all fields before submitting'));
      return;
    }
    setSaving(true);
    try {
      // Build legacy fields for backward compat + new fields.
      const agentCommByLevel = {};
      let anyAgentComm = false;
      selectedLevels.forEach(lvl => {
        agentCommByLevel[lvl] = renewalStates[lvl] === 'yes';
        if (agentCommByLevel[lvl]) anyAgentComm = true;
      });
      await axios.post(
        `${API}/admin/commission-audit/${subjectId}/mark-audited`,
        {
          file_id:                    uploadedFile.file_id,
          filename:                   uploadedFile.filename,
          note:                       (note || '').trim(),
          subject_type:               subjectType,
          is_renewal:                 isRenewal === 'yes',
          renewal_level:              selectedLevels.length ? Math.max(...selectedLevels) : null,
          renewal_levels:             selectedLevels,
          renewal_states:             renewalStates,
          has_agent_commission:       anyAgentComm,
          agent_commission_by_level:  agentCommByLevel,
          receipt_amount:             aiResult?.amount || null,
        },
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم التدقيق بنجاح', 'Marked as audited'));
      navigate('/manage/cloudgm-agent/commission-audit');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const unmark = async () => {
    if (!window.confirm(t('إلغاء التدقيق لهذا الطلب؟', 'Revert audit for this row?'))) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/commission-audit/${subjectId}/unmark`,
        {},
        { headers: getAuthHeaders(), params: { subject_type: subjectType } },
      );
      toast.success(t('تم إلغاء التدقيق', 'Audit reverted'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية',                     label_en: 'Home',                path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)',     label_en: 'CLOUDGM FZ LLC',       path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent',       label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'التدقيق على العمولة المحصلة',  label_en: 'Commission Audit',     path: '/manage/cloudgm-agent/commission-audit' },
    { label_ar: summary?.request_no || '…',      label_en: summary?.request_no || '…', path: null },
  ];

  if (loading || !summary) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/commission-audit')}>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-yellow-400" />
        </div>
      </AgentPageShell>
    );
  }

  const isAudited = !!audit?.audited;

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/commission-audit')}>
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="rounded-2xl border border-yellow-500/25 bg-gradient-to-br from-yellow-500/10 to-transparent p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-yellow-500/15 border border-yellow-500/30 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-yellow-300" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold text-white" data-testid="audit-detail-title">
                {t('تدقيق العمولة', 'Commission Audit')} — {summary.request_no}
              </h1>
              <p className="text-xs text-white/60 mt-0.5">
                {summary.company_name || summary.service_name}
              </p>
            </div>
            {isAudited && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                    data-testid="audit-status-audited">
                <CheckCircle2 className="h-3 w-3" />
                {t('تم التدقيق', 'Audited')}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <div className="rounded-xl border p-3 bg-yellow-500/10 border-yellow-500/30">
              <div className="text-[10px] uppercase font-bold tracking-wider text-yellow-300 mb-1">
                {t('عمولة الشركة', 'Company Commission')}
              </div>
              <div className="text-white font-mono font-bold text-lg" data-testid="audit-company-commission">
                {fmt(summary.company_commission)} <span className="text-white/50 text-[10px]">AED</span>
              </div>
            </div>
            <div className="rounded-xl border p-3 bg-cyan-500/10 border-cyan-500/30">
              <div className="text-[10px] uppercase font-bold tracking-wider text-cyan-300 mb-1">
                {t('عمولة الوكيل', 'Agent Commission')}
              </div>
              <div className="text-white font-mono font-bold text-lg" data-testid="audit-agent-commission">
                {summary.agent?.mode === 'none'
                  ? <span className="text-white/40 italic text-sm">{t('بدون وكيل', 'No agent')}</span>
                  : `${fmt(summary.agent_commission)} `}
                {summary.agent?.mode !== 'none' && <span className="text-white/50 text-[10px]">AED</span>}
              </div>
            </div>
            <div className="rounded-xl border p-3 bg-white/[0.02] border-white/10">
              <div className="text-[10px] uppercase font-bold tracking-wider text-white/60 mb-1">
                {t('الوكيل', 'Agent')}
              </div>
              <div className="text-white text-sm truncate flex items-center gap-1">
                <User className="h-3.5 w-3.5 text-white/40" />
                {summary.agent?.name || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Step 1 — Upload */}
        <SectionCard title={t('١. سند الإيداع (يطابق عمولة الشركة)', '1. Deposit receipt (must match company commission)')} icon={Upload} color="#eab308">
          {uploadedFile ? (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-center gap-2 text-sm text-emerald-200" data-testid="audit-uploaded-block">
              <CheckCircle2 className="h-4 w-4" />
              <span className="flex-1 truncate">{uploadedFile.filename}</span>
              {!isAudited && (
                <button
                  onClick={clearFile}
                  className="text-[11px] px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 text-white/70"
                  data-testid="audit-clear-file"
                >
                  {t('استبدال', 'Replace')}
                </button>
              )}
            </div>
          ) : (
            <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white cursor-pointer text-sm">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin text-yellow-300" /> : <Upload className="h-4 w-4 text-yellow-300" />}
              {uploading ? t('يرفع…', 'Uploading…') : t('رفع الإيصال', 'Upload receipt')}
              <input type="file" accept="image/*,application/pdf" className="hidden"
                     onChange={(e) => doUploadAndVerify(e.target.files?.[0])}
                     data-testid="audit-upload-input" />
            </label>
          )}
          {aiScanning && (
            <p className="text-xs mt-2 text-yellow-300 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {t('AI يتحقق من المبلغ…', 'AI verifying amount…')}</p>
          )}
          {aiResult && (
            <p
              className="text-xs mt-2"
              style={{ color: aiResult.verified ? '#10b981' : '#ef4444' }}
              data-testid="audit-ai-info"
            >
              {aiResult.info}
            </p>
          )}
        </SectionCard>

        {/* Step 2 — MERGED (iter-278b+iter-279c, Feb 2026): Multi-select renewals
             with per-renewal agent-commission Y/N.
             Users can select MULTIPLE renewal indexes and answer the agent
             question independently for each — critical when older renewals
             had agent commission and newer ones don't (or vice versa). */}
        <SectionCard title={t('٢. هل يوجد عمولة على تجديد الرخصة؟',
                              '2. Is there a commission on the license renewal?')}
                     icon={RefreshCw} color="#06b6d4">
          <YesNo
            value={isRenewal}
            onChange={(v) => {
              setIsRenewal(v);
              if (v === 'no') setRenewalStates({});
            }}
            t={t}
            disabled={isAudited}
            testIdPrefix="audit-renewal"
          />

          {isRenewal === 'yes' && (
            <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
              <p className="text-xs text-white/60 mb-2">
                {t('اختر التجديدات التي تنطبق (متعدد) ثم حدد لكل تجديد إذا كان للوكيل عمولة أم لا',
                   'Pick all renewals that apply (multi-select) and answer the agent-commission question for each')}
              </p>
              <div className="grid grid-cols-1 gap-2">
                {[1, 2, 3, 4, 5].map((n) => {
                  const selected = Object.prototype.hasOwnProperty.call(renewalStates, n);
                  const agentAns = renewalStates[n];
                  const arNames = ['الأول', 'الثاني', 'الثالث', 'الرابع', 'الخامس'];
                  return (
                    <div key={n} className={`rounded-lg border-2 transition-all
                        ${selected ? 'bg-cyan-500/[0.06] border-cyan-500/40'
                                   : 'bg-[#0a0a0a] border-[#2a2a4e]'}`}>
                      <button
                        type="button"
                        disabled={isAudited}
                        onClick={() => {
                          setRenewalStates(prev => {
                            const next = { ...prev };
                            if (selected) delete next[n]; else next[n] = null;
                            return next;
                          });
                        }}
                        className="w-full flex items-center justify-between p-3 text-sm disabled:opacity-50"
                        data-testid={`audit-renewal-level-${n}`}
                      >
                        <span className={selected ? 'text-cyan-200 font-bold' : 'text-white/70'}>
                          {selected ? '☑' : '☐'} {t(`التجديد ${arNames[n-1]}`, `Renewal #${n}`)}
                        </span>
                        {selected && (
                          <span className="text-[10px] text-white/50">
                            {agentAns === 'yes' ? t('عمولة الوكيل: نعم', 'Agent commission: Yes')
                              : agentAns === 'no' ? t('عمولة الوكيل: لا', 'Agent commission: No')
                              : t('اختر إجابة الوكيل ↓', 'Answer agent Q ↓')}
                          </span>
                        )}
                      </button>
                      {selected && (
                        <div className="px-3 pb-3 pt-2 border-t border-cyan-500/20 bg-cyan-500/[0.03]">
                          <p className="text-[11px] text-cyan-200 mb-2 font-medium">
                            {t(`هل يوجد عمولة للوكيل على التجديد ${arNames[n-1]}؟`,
                               `Is there an agent commission on renewal #${n}?`)}
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { key: 'yes', ar: 'نعم', en: 'Yes', color: 'emerald' },
                              { key: 'no',  ar: 'لا',  en: 'No',  color: 'red' },
                            ].map(o => (
                              <button
                                key={o.key}
                                type="button"
                                disabled={isAudited}
                                onClick={() => setRenewalStates(prev => ({ ...prev, [n]: o.key }))}
                                className={`py-1.5 rounded-md border text-xs font-bold
                                  ${agentAns === o.key
                                    ? (o.color === 'emerald'
                                       ? 'bg-emerald-500/20 border-emerald-500 text-emerald-200'
                                       : 'bg-red-500/20 border-red-500 text-red-200')
                                    : 'bg-[#050505] border-white/10 text-white/60 hover:border-white/30'}`}
                                data-testid={`audit-agent-comm-${n}-${o.key}`}
                              >
                                {t(o.ar, o.en)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {Object.keys(renewalStates).length === 0 && (
                <div className="text-[11px] text-amber-300 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  {t('اختر تجديداً واحداً على الأقل', 'Pick at least one renewal')}
                </div>
              )}
            </div>
          )}
        </SectionCard>

        {/* Optional note */}
        <SectionCard title={t('ملاحظة (اختياري)', 'Note (optional)')} icon={Building2} color="#71717A">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isAudited}
            placeholder={t('اكتب ملاحظة تظهر في سجل التدقيق', 'A note that will appear in the audit record')}
            className="bg-black/40 border-white/10 text-white text-sm"
            data-testid="audit-note-input"
          />
        </SectionCard>

        {/* Action bar */}
        <div className="flex items-center justify-between gap-3 pb-8">
          <Button
            onClick={() => navigate('/manage/cloudgm-agent/commission-audit')}
            variant="outline"
            className="border-white/10 text-white/70"
            data-testid="audit-back-btn"
          >
            <ArrowLeft className="h-4 w-4 me-1" />
            {t('رجوع', 'Back')}
          </Button>
          {isAudited ? (
            <Button
              onClick={unmark}
              disabled={saving}
              variant="outline"
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-2"
              data-testid="audit-unmark-btn"
            >
              <RotateCw className="h-4 w-4" />
              {t('إلغاء التدقيق', 'Revert audit')}
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={saving || !canSubmit}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 disabled:opacity-40"
              data-testid="audit-submit-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {t('تم التدقيق', 'Mark Audited')}
            </Button>
          )}
        </div>
      </div>
    </AgentPageShell>
  );
}

function SectionCard({ title, icon: Icon, color, children }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`section-${title}`}>
      <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" style={{ color }} />}
        {title}
      </h3>
      {children}
    </section>
  );
}

function YesNo({ value, onChange, t, disabled, testIdPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('yes')}
        className={
          'p-3 rounded-xl border-2 text-sm font-semibold transition-all disabled:opacity-50 flex items-center gap-2 justify-center ' +
          (value === 'yes'
            ? 'bg-emerald-500/15 border-emerald-500 text-emerald-300'
            : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
        }
        data-testid={`${testIdPrefix}-yes`}
      >
        <CheckCircle2 className="h-4 w-4" />
        {t('نعم', 'Yes')}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('no')}
        className={
          'p-3 rounded-xl border-2 text-sm font-semibold transition-all disabled:opacity-50 flex items-center gap-2 justify-center ' +
          (value === 'no'
            ? 'bg-rose-500/15 border-rose-500 text-rose-300'
            : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
        }
        data-testid={`${testIdPrefix}-no`}
      >
        <XCircle className="h-4 w-4" />
        {t('لا', 'No')}
      </button>
    </div>
  );
}
